/// <reference types="vite/client" />
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { configureVeriDocClient } from '@veridoc/ui';
import { isApiError } from '@joint/jerry/api';
import { AUTH_TOKEN_KEY } from '@joint/jerry/constants';
import { AppRoutes } from './routes';

dayjs.locale('zh-cn');

// Import Jerry design tokens globally
import '@joint/jerry/styles/tokens.css';

// Tailwind utilities for veri_doc @veridoc/ui components (preflight disabled,
// see tailwind.config.ts). Mounted after tokens so antd reset still wins.
import './veridoc-tailwind.css';

// ---------------------------------------------------------------------------
// VeriDoc transport seam (ADR-119 D2)
// ---------------------------------------------------------------------------
// The host (jerry) mounts veridoc's engine under `/api/v1/jerry/vd` and
// authenticates with a JWT bearer token stored in localStorage. We inject both
// before any `@veridoc/ui` component mounts so the workspace's first request
// carries the right credentials. `credentials: 'omit'` + `csrf: false` because
// the host uses bearer auth, not the cookie auth veridoc's demo defaults to.
configureVeriDocClient({
  baseUrl: '/api/v1/jerry/vd',
  credentials: 'omit',
  csrf: false,
  fetch: (input, init) => {
    const token =
      typeof localStorage !== 'undefined'
        ? (localStorage.getItem(AUTH_TOKEN_KEY) ?? '')
        : '';
    const headers = new Headers(init?.headers ?? {});
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(input, { ...init, headers });
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        if (isApiError(error) && [401, 403, 404].includes(error.status)) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error(`[Query Error] ${JSON.stringify(query.queryKey)}:`, error);
    },
  }),
});

// Map Jerry design tokens to Ant Design theme
const jerryTheme = {
  token: {
    colorPrimary: '#7C3AED',          // --jerry-primary-500
    colorInfo: '#0052D9',             // --jerry-info（语义 Info 固定蓝）
    colorSuccess: '#00A870',          // --jerry-success
    colorWarning: '#ED7B2F',          // --jerry-warning
    colorError: '#E34D59',            // --jerry-danger
    colorLink: '#7C3AED',             // 链接色跟随主色
    colorBgContainer: '#FFFFFF',       // --jerry-surface-card
    colorBorder: '#E3E6EB',            // --jerry-gray-200
    colorText: '#1A1F27',             // --jerry-text-primary
    colorTextSecondary: '#636B75',    // --jerry-text-secondary
    borderRadius: 6,                  // --jerry-r-md
    fontFamily: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
    fontSize: 14,
  },
  components: {
    Button: {
      primaryShadow: '0 2px 0 rgba(124, 58, 237, 0.1)',
    },
    Input: {
      activeBorderColor: '#7C3AED',
      hoverBorderColor: '#AD8EE1',
    },
    Select: {
      activeBorderColor: '#7C3AED',
    },
  },
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <ConfigProvider locale={zhCN} theme={jerryTheme}>
      <AntdApp>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  </QueryClientProvider>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
